import { Registry, type Identified } from './Registry';

/**
 * Boss tanımları.
 * Saldırı setleri ve faz geçişleri veriden gelir; savaş kodu değişmeden
 * yeni boss eklenebilir.
 *
 * Not: Görsel dil kan/parçalanma içermez. Vurgu siyah duman, enerji,
 * parçacık, ekran sarsıntısı ve ses üzerinedir.
 */

export type AttackShape = 'melee' | 'lunge' | 'slam' | 'shockwave' | 'projectile' | 'summon';

export interface BossAttack extends Identified {
  name: string;
  shape: AttackShape;
  damage: number;
  /** Saldırı öncesi hazırlık (oyuncuya tepki penceresi). */
  windup: number;
  /** Etki süresi. */
  active: number;
  cooldown: number;
  range: number;
  /** Alan saldırısı yarıçapı. */
  radius?: number;
  /** Ekran sarsıntısı şiddeti 0..1. */
  shake?: number;
  /** Parçacık/efekt anahtarı. */
  vfx?: string;
  telegraph?: string;
}

export interface BossPhase {
  /** Bu faza geçiş için can oranı eşiği (1 = savaş başı). */
  hpAbove: number;
  attacks: string[];
  moveSpeed: number;
  /** Faza girerken oynatılacak efekt. */
  enterVfx?: string;
  enrage?: number;
}

export interface BossDef extends Identified {
  name: string;
  title: string;
  health: number;
  defense: number;
  size: number;
  color: number;
  attacks: BossAttack[];
  phases: BossPhase[];
  /** Yenildiğinde verilen ödüller. */
  rewards: Array<{ item: string; count: number; guaranteed?: boolean }>;
  kogox: [number, number];
  arena: { radius: number; height: number };
  music?: string;
  /** Boss ölçeklemesi zorluktan bağımsız: endgame karşılaşması. */
  ignoreDifficultyScaling?: boolean;
}

export const bosses = new Registry<BossDef>('bosses');

bosses.register({
  id: 'ares',
  name: 'ARES',
  title: 'Uyuyan Dev',
  health: 4200,
  defense: 18,
  size: 6.5,
  color: 0x2a2a35,
  arena: { radius: 46, height: 22 },
  music: 'boss_ares',
  ignoreDifficultyScaling: true,
  attacks: [
    {
      id: 'claw',
      name: 'Pençe',
      shape: 'melee',
      damage: 85,
      windup: 0.45,
      active: 0.2,
      cooldown: 1.6,
      range: 9,
      shake: 0.25,
      vfx: 'smoke_slash',
      telegraph: 'Pençesini kaldırıyor',
    },
    {
      id: 'charge',
      name: 'Koşarak Çarpma',
      shape: 'lunge',
      damage: 140,
      windup: 1,
      active: 1.4,
      cooldown: 6,
      range: 40,
      shake: 0.45,
      vfx: 'smoke_trail',
      telegraph: 'Sana doğru eğiliyor',
    },
    {
      id: 'leap_slam',
      name: 'Yüksek Sıçrama',
      shape: 'slam',
      damage: 260,
      windup: 1.3,
      active: 0.4,
      cooldown: 9,
      range: 45,
      radius: 14,
      shake: 0.8,
      vfx: 'ground_burst',
      telegraph: 'Gökyüzüne sıçradı!',
    },
    {
      id: 'shock_ring',
      name: 'Enerji Dalgası',
      shape: 'shockwave',
      damage: 120,
      windup: 1.1,
      active: 0.8,
      cooldown: 11,
      range: 0,
      radius: 30,
      shake: 0.55,
      vfx: 'ring_wave',
      telegraph: 'Zemin titriyor',
    },
    {
      id: 'smoke_bolt',
      name: 'Duman Mızrağı',
      shape: 'projectile',
      damage: 95,
      windup: 0.8,
      active: 0.1,
      cooldown: 5,
      range: 60,
      shake: 0.15,
      vfx: 'smoke_bolt',
      telegraph: 'Duman yoğunlaşıyor',
    },
    {
      id: 'oblivion',
      name: 'YOK EDİCİ',
      shape: 'slam',
      // Tasarım gereği: en az 1000 hasar veren özel saldırı.
      damage: 1000,
      windup: 3.2,
      active: 0.6,
      cooldown: 34,
      range: 60,
      radius: 26,
      shake: 1,
      vfx: 'oblivion',
      telegraph: 'ARES kükrüyor — KAÇ!',
    },
  ],
  phases: [
    { hpAbove: 0.7, attacks: ['claw', 'charge'], moveSpeed: 6.5 },
    { hpAbove: 0.45, attacks: ['claw', 'charge', 'leap_slam'], moveSpeed: 7.5, enterVfx: 'smoke_burst' },
    { hpAbove: 0.2, attacks: ['claw', 'charge', 'leap_slam', 'shock_ring', 'smoke_bolt'], moveSpeed: 8.5, enterVfx: 'smoke_burst', enrage: 1.15 },
    { hpAbove: 0, attacks: ['claw', 'charge', 'leap_slam', 'shock_ring', 'smoke_bolt', 'oblivion'], moveSpeed: 9.5, enterVfx: 'oblivion_charge', enrage: 1.3 },
  ],
  rewards: [
    { item: 'kug_sword', count: 1, guaranteed: true },
    { item: 'blackDiamond', count: 3 },
    { item: 'voidsteel', count: 4 },
  ],
  kogox: [1500, 3000],
});

bosses.register({
  id: 'red_king',
  name: 'KIZIL KRAL',
  title: 'Kızıl Kale’nin Sahibi',
  health: 1800,
  defense: 12,
  size: 2.6,
  color: 0x9b1b1b,
  arena: { radius: 26, height: 14 },
  attacks: [
    { id: 'sword', name: 'Kılıç', shape: 'melee', damage: 55, windup: 0.4, active: 0.2, cooldown: 1.4, range: 4, shake: 0.2 },
    { id: 'summon_guard', name: 'Muhafız Çağır', shape: 'summon', damage: 0, windup: 1.5, active: 0.2, cooldown: 20, range: 0, shake: 0.2, vfx: 'summon' },
    { id: 'crush', name: 'Ezici Vuruş', shape: 'slam', damage: 150, windup: 1.2, active: 0.3, cooldown: 10, range: 12, radius: 8, shake: 0.6, vfx: 'ground_burst' },
  ],
  phases: [
    { hpAbove: 0.5, attacks: ['sword', 'summon_guard'], moveSpeed: 4.2 },
    { hpAbove: 0, attacks: ['sword', 'summon_guard', 'crush'], moveSpeed: 5, enterVfx: 'smoke_burst', enrage: 1.2 },
  ],
  rewards: [
    { item: 'diamond', count: 6, guaranteed: true },
    { item: 'power_feet', count: 1 },
  ],
  kogox: [600, 1400],
});
